import React, { useEffect, useState } from "react";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import {
  fetchCourierFeeSettings,
  resetSettingsState,
  updateCourierFeeSettingsThunk,
} from "../../../../redux/features/settingsSlice";
import { mapDataToLabelValue } from "../../../../helpers";
import { toast } from "react-toastify";
import { courierFeeValidationSchema } from "./schemas";

interface CourierFeeForm {
  courierId: string;
  baseFee: string;
  perKmCharge: string;
  weightFactor: string;
  insurancePercentage: string;
}

interface EditCourierPricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditCourierPricingModal: React.FC<EditCourierPricingModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { couriers } = useAppSelector((state) => state.courier);
  const { courierFee, loading } = useAppSelector(
    (state: RootState) => state.settings
  );

  const [formData, setFormData] = useState<CourierFeeForm>({
    courierId: "",
    baseFee: "",
    perKmCharge: "",
    weightFactor: "",
    insurancePercentage: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof CourierFeeForm, string>>
  >({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: Number(value) < 0 ? 0 : value,
    }));
  };

  const handleSelectChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      courierId: value,
    }));
  };

  const handleDiscard = () => {
    setFormData({
      courierId: "",
      baseFee: "",
      perKmCharge: "",
      weightFactor: "",
      insurancePercentage: "",
    });
    dispatch(resetSettingsState());
    onClose();
  };

  const validateForm = async () => {
    try {
      await courierFeeValidationSchema.validate(formData, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err: any) {
      const validationResult: Partial<Record<keyof CourierFeeForm, string>> =
        {};
      err.inner.forEach((e: any) => {
        validationResult[e.path as keyof CourierFeeForm] = e.message;
      });
      setErrors(validationResult);
      return false;
    }
  };

  const handleSave = async () => {
    const isValid = await validateForm();
    if (isValid) {
      const payload = {
        courierId: formData.courierId,
        baseFee: parseFloat(formData.baseFee),
        perKmCharge: parseFloat(formData.perKmCharge),
        weightFactor: parseFloat(formData.weightFactor),
        insurancePercentage: parseFloat(formData.insurancePercentage),
      };
      dispatch(updateCourierFeeSettingsThunk(payload))
        .unwrap()
        .then(() => {
          handleDiscard();
          toast.success("Courier pricing updated successfully");
        });
    }
  };

  useEffect(() => {
    if (formData.courierId) {
      dispatch(fetchCourierFeeSettings(formData.courierId));
    }
  }, [formData.courierId, dispatch]);

  useEffect(() => {
    if (courierFee) {
      setFormData((prev) => ({
        ...prev,
        baseFee: courierFee.baseFee?.toString() || "",
        perKmCharge: courierFee.perKmCharge?.toString() || "",
        weightFactor: courierFee.weightFactor?.toString() || "",
        insurancePercentage: courierFee.insurancePercentage?.toString() || "",
      }));
    }
  }, [courierFee]);

  if (!isOpen) return null;

  return (
    <CustomModalContainer
      title="Edit Courier Pricing"
      open={isOpen}
      onClose={handleDiscard}
      footer={
        <div className="flex justify-between">
          <Button
            label="Discard"
            type="button"
            onClick={handleDiscard}
            customStyle={{
              width: "auto",
              minWidth: "120px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Save Changes"
            type="button"
            onClick={handleSave}
            customStyle={{ width: "auto", minWidth: "150px" }}
            isLoading={loading}
          />
        </div>
      }
    >
      <div className="p-6 space-y-4 w-full">
        <Dropdown
          label="Select Courier"
          placeholder="Select Courier"
          options={mapDataToLabelValue(couriers?.data?.items, "name", "id")}
          value={formData.courierId}
          onSelect={handleSelectChange}
          error={errors.courierId}
          required
        />
        <ModalInput
          label="Base Fee (₦)"
          name="baseFee"
          value={formData.baseFee}
          onChange={handleInputChange}
          type="number"
          error={errors.baseFee}
          required
        />
        <ModalInput
          label="Per KM Charge (₦)"
          name="perKmCharge"
          value={formData.perKmCharge}
          onChange={handleInputChange}
          type="number"
          error={errors.perKmCharge}
          required
        />
        <ModalInput
          label="Weight Factor"
          name="weightFactor"
          value={formData.weightFactor}
          onChange={handleInputChange}
          type="number"
          error={errors.weightFactor}
          required
        />
        <ModalInput
          label="Insurance Percentage (%)"
          name="insurancePercentage"
          value={formData.insurancePercentage}
          onChange={handleInputChange}
          type="number"
          error={errors.insurancePercentage}
          required
        />
      </div>
    </CustomModalContainer>
  );
};

export default EditCourierPricingModal;
